import { type Row, type Table as TanstackTable, flexRender } from '@tanstack/react-table';
import * as React from 'react';

import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/components/data-table/data-table-toolbar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getCommonPinningStyles } from '@/lib/data-table';
import { cn } from "@/lib/utils";

interface DataTableProps<TData> extends React.ComponentProps<'div'> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  isLoading?: boolean;
  skeletonRows?: React.ReactNode;
  onRefresh?: () => void;
  onImport?: () => void;
  onExport?: () => void;
  onRowClick?: (row: TData) => void;
  isDraft?: (row: TData) => boolean;
  showFilters?: boolean;
  extraFilters?: React.ReactNode;
  showToolbar?: boolean;
  showPagination?: boolean;
  /**
   * Optional custom renderer for mobile card view (< md breakpoint).
   * When omitted, a default stacked label/value layout is used.
   */
  mobileCardRender?: (row: Row<TData>) => React.ReactNode;
  /**
   * Disable the automatic mobile card view and always render the table.
   */
  disableMobileCards?: boolean;
}

export function DataTable<TData>({
  table,
  actionBar,
  children,
  isLoading = false,
  skeletonRows,
  onRefresh,
  onImport,
  onExport,
  onRowClick,
  isDraft,
  showFilters = true,
  extraFilters,
  showPagination = true,
  showToolbar = true,
  mobileCardRender,
  disableMobileCards = false,
  className,
  ...props
}: DataTableProps<TData>) {
  const rows = table.getRowModel().rows;

  const handleRowInteraction = (row: Row<TData>, e: React.MouseEvent) => {
    if (table.getSelectedRowModel().rows.length > 0) {
      row.toggleSelected(row.getIsSelected() ? false : true);
      return;
    }
    const target = e.target as HTMLElement;
    const isInteractiveElement =
      target.tagName === 'BUTTON' ||
      target.tagName === 'A' ||
      target.tagName === 'INPUT' ||
      target.closest('button:not([role="row"])') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('[role="checkbox"]') ||
      target.closest('[role="menuitem"]') ||
      target.closest('[data-radix-collection-item]');
    if (isInteractiveElement) return;
    onRowClick?.(row.original);
  };

  const defaultMobileCardRender = (row: Row<TData>) => {
    const cells = row.getVisibleCells();
    if (!cells.length) return null;
    const [first, ...rest] = cells;
    const getLabel = (col: (typeof cells)[number]['column']) => {
      const meta = col.columnDef.meta as { label?: string } | undefined;
      if (meta?.label) return meta.label;
      const header = col.columnDef.header;
      if (typeof header === 'string') return header;
      return col.id;
    };
    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium">
          {flexRender(first.column.columnDef.cell, first.getContext())}
        </div>
        {rest.length > 0 && (
          <dl className="grid grid-cols-[minmax(6rem,max-content)_1fr] gap-x-3 gap-y-1.5 text-xs">
            {rest.map((cell) => {
              const label = getLabel(cell.column);
              return (
                <React.Fragment key={cell.id}>
                  <dt className="text-muted-foreground truncate">{label}</dt>
                  <dd className="min-w-0 text-right">
                    <div className="flex items-center justify-end">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </dd>
                </React.Fragment>
              );
            })}
          </dl>
        )}
      </div>
    );
  };

  const renderMobileCard = mobileCardRender || defaultMobileCardRender;

  return (
    <div className={cn('flex w-full flex-col gap-2.5', className)} {...props}>
      {showToolbar && (
        <DataTableToolbar
          table={table}
          isLoading={isLoading}
          onRefresh={onRefresh}
          onImport={onImport}
          onExport={onExport}
          showFilters={showFilters}
          extraFilters={extraFilters}
        >
          {children}
        </DataTableToolbar>
      )}
      {!disableMobileCards && (
        <div className="flex flex-col gap-2 md:hidden">
          {isLoading ? (
            <div className="border-border/60 bg-card rounded-lg border p-6 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : rows.length === 0 ? (
            <div className="border-border/60 bg-card rounded-lg border p-6 text-center text-sm text-muted-foreground">
              No results.
            </div>
          ) : (
            rows.map((row) => {
              const isRowDraft = isDraft?.(row.original) ?? false;
              const isClickable = !!onRowClick;
              return (
                <div
                  key={row.id}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={isClickable ? (e) => handleRowInteraction(row, e) : undefined}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick?.(row.original);
                    }
                  }}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    'border-border/60 rounded-lg border p-3 shadow-sm transition-colors',
                    isRowDraft
                      ? 'bg-orange-50 dark:bg-orange-950/20'
                      : 'bg-card',
                    isClickable && 'active:bg-accent/50 cursor-pointer hover:bg-accent/30',
                    row.getIsSelected() && 'ring-primary ring-2',
                  )}
                >
                  {renderMobileCard(row)}
                </div>
              );
            })
          )}
        </div>
      )}
      <div
        className={cn(
          'border-border/60 bg-card overflow-hidden rounded-lg border shadow-sm',
          !disableMobileCards && 'hidden md:block',
        )}
      >
        <div className="block w-full">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        ...getCommonPinningStyles({ column: header.column }),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Show skeleton rows when loading
                skeletonRows || (
                  <TableRow>
                    <TableCell
                      colSpan={table.getAllColumns().length}
                      className="text-muted-foreground h-24 text-center"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                )
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  // Check if the row is a draft order using the type-safe isDraft prop
                  const isRowDraft = isDraft?.(row.original) ?? false;
                  const isClickable = !!onRowClick;

                  const handleRowClick = (e: React.MouseEvent) => {
                    if (table.getSelectedRowModel().rows.length > 0) {
                      row.toggleSelected(row.getIsSelected() ? false : true);
                      return;
                    }
                    // Don't trigger row click if user clicked on interactive elements
                    const target = e.target as HTMLElement;

                    // Check if click is on an actual button, link, input, or inside a dropdown
                    // But exclude the row itself and regular cells
                    const isInteractiveElement =
                      target.tagName === 'BUTTON' ||
                      target.tagName === 'A' ||
                      target.tagName === 'INPUT' ||
                      target.closest('button:not([role="row"])') ||
                      target.closest('a') ||
                      target.closest('input') ||
                      target.closest('[role="checkbox"]') ||
                      target.closest('[role="menuitem"]') ||
                      target.closest('[data-radix-collection-item]');

                    if (isInteractiveElement) {
                      return;
                    }

                    onRowClick?.(row.original);
                  };

                  const handleKeyDown = (e: React.KeyboardEvent) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick(row.original);
                    }
                  };

                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      onClick={onRowClick ? handleRowClick : undefined}
                      onKeyDown={handleKeyDown}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      className={cn(
                        isRowDraft
                          ? 'bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/20 dark:hover:bg-orange-900/30'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-800',
                        onRowClick && 'cursor-pointer',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            ...getCommonPinningStyles({ column: cell.column }),
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className="text-muted-foreground h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className="flex flex-col gap-2.5">{showPagination && <DataTablePagination table={table} />}</div>
      {actionBar}
    </div>
  );
}
